import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { FileText } from "lucide-react";
import {
  formatMoney,
  EINVOICE_STATUS_LABELS,
  EINVOICE_STATUS_TONE,
} from "@finora/config";
import { EInvoiceDirection, EInvoiceStatus } from "@finora/types";
import { EmptyState, Skeleton, StatusBadge } from "@finora/ui";
import { api } from "../../lib/api";
import { PurchaseInvoicesTab } from "./PurchaseInvoicesTab";

type TabKey = "inbound" | "outbound" | "pending" | "rejected";

// "Ulazne" (razgovor 28.09.2026, Task 5) - istraženo: backend (efaktura
// modul) implementira SAMO odlazno slanje/status-refresh/otkazivanje ka
// SEF-u (SefIntegrationService.sendSalesDocument/refreshStatus/cancel).
// NE POSTOJI nikakav prijem/polling/webhook sa SEF-a koji bi ikad upisao
// EInvoice red sa direction=INBOUND - tab bi zato UVEK bio prazan, ne
// zato što prikazuje netačne podatke, već zato što nema izvora podataka.
// Tab je zato onemogućen (sa "Uskoro" napomenom) umesto da tiho ostane
// zauvek prazan bez objašnjenja. "Čekaju odluku" NIJE dirano - taj tab
// filtrira status stvarno poslatih (odlaznih) faktura, što već radi.
const TABS: { key: TabKey; label: string; disabled?: boolean }[] = [
  { key: "inbound", label: "Ulazne" },
  { key: "outbound", label: "Izlazne" },
  { key: "pending", label: "Čekaju odluku" },
  { key: "rejected", label: "Odbijene" },
];

export function EfakturaPage() {
  const [tab, setTab] = useState<TabKey>("inbound");
  const [openId, setOpenId] = useState<string | null>(null);
  const items = useQuery({
    queryKey: ["e-invoice-items", openId],
    queryFn: () => api.getEInvoiceItems(openId as string),
    enabled: !!openId,
  });
  const { data, isLoading } = useQuery({
    queryKey: ["e-invoices"],
    queryFn: () => api.listEInvoices(),
  });

  const filtered = useMemo(() => {
    if (!data) return [];
    switch (tab) {
      case "inbound":
        return data.filter((d) => d.direction === EInvoiceDirection.INBOUND);
      case "outbound":
        return data.filter((d) => d.direction === EInvoiceDirection.OUTBOUND);
      case "pending":
        return data.filter((d) => d.status === EInvoiceStatus.PENDING_DECISION);
      case "rejected":
        return data.filter((d) => d.status === EInvoiceStatus.REJECTED);
    }
  }, [data, tab]);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">eFakture</h1>
        <p className="text-sm text-slate-500">
          Pregled elektronskih faktura. Slanje na SEF radi kroz "Pošalji na SEF" dugme na strani
          fakture (podešavanja API ključa su na strani Podešavanja → SEF). Ulazne fakture sa SEF-a
          mogu se označiti kao trošak (kartica "Ulazne").
        </p>
      </div>

      <div className="flex gap-1 border-b border-border">
        {TABS.map((t) => (
          <button
            key={t.key}
            disabled={t.disabled}
            onClick={() => !t.disabled && setTab(t.key)}
            title={t.disabled ? "Prijem ulaznih faktura sa SEF-a još nije povezan - uskoro" : undefined}
            className={`flex items-center gap-1.5 border-b-2 px-4 py-2 text-sm font-medium ${
              t.disabled
                ? "cursor-not-allowed border-transparent text-slate-300"
                : tab === t.key
                  ? "border-brand text-brand"
                  : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            {t.label}
            {t.disabled && (
              <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-slate-400">
                Uskoro
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === "inbound" ? (
        <PurchaseInvoicesTab />
      ) : (
      <div className="rounded-xl border border-border bg-surface shadow-subtle">
        {isLoading ? (
          <div className="space-y-2 p-5">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-5">
            <EmptyState icon={FileText} title="Nema dokumenata u ovoj kategoriji" />
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Broj</th>
                <th>Partner</th>
                <th>Datum izdavanja</th>
                <th>Iznos</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((inv) => (
                <tr key={inv.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setOpenId(inv.id)}>
                  <td className="font-medium text-slate-800">{inv.documentNumber}</td>
                  <td className="text-slate-600">{inv.counterpartyName}</td>
                  <td className="text-slate-500">{new Date(inv.issueDate).toLocaleDateString("sr-RS")}</td>
                  <td>{formatMoney(inv.grossAmount, inv.currency)}</td>
                  <td>
                    <StatusBadge label={EINVOICE_STATUS_LABELS[inv.status]} tone={EINVOICE_STATUS_TONE[inv.status]} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      )}
      {openId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setOpenId(null)}>
          <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-xl bg-surface p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            {items.isLoading || !items.data ? (
              <Skeleton className="h-40 w-full" />
            ) : (
              <div className="space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-base font-semibold text-slate-900">Faktura {items.data.number}</h3>
                    <p className="text-sm text-slate-500">{items.data.counterpartyName} · {new Date(items.data.issueDate).toLocaleDateString("sr-RS")}</p>
                  </div>
                  <button className="text-sm text-slate-500 hover:text-slate-800" onClick={() => setOpenId(null)}>Zatvori</button>
                </div>
                {items.data.lines.length === 0 ? (
                  <p className="text-sm text-slate-500">Za ovu fakturu nema sačuvanih stavki.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase text-slate-500">
                        <th>Stavka</th><th className="text-right">Količina</th><th className="text-right">Cena</th><th className="text-right">Popust</th><th className="text-right">Iznos</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.data.lines.map((l, i) => (
                        <tr key={i} className="border-t border-border">
                          <td className="py-1.5">{l.name}</td>
                          <td className="text-right">{Number(l.quantity)} {l.unit}</td>
                          <td className="text-right">{formatMoney(l.unitPriceGross, items.data!.currency)}</td>
                          <td className="text-right">{Number(l.discountAmount) ? formatMoney(l.discountAmount, items.data!.currency) : "—"}</td>
                          <td className="text-right font-medium">{formatMoney(l.grossAmount, items.data!.currency)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                <div className="flex justify-end text-sm font-semibold">Ukupno: {formatMoney(items.data.grossAmount, items.data.currency)}</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
